/**
 * Civilian Nights — Pay & Reconcile organiser panel.
 *
 * Renders below the attendee list in EventDetailsPage when the viewer
 * is the event organiser AND `organiser_collects_money` is true. Lets
 * the organiser see each going-attendee's payment status, mark them
 * as Received (money landed in your account) or Refunded.
 */
import { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, RotateCcw, Loader2, PoundSterling } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_PILLS = {
  unpaid: { label: 'Unpaid', cls: 'bg-amber-500/20 text-amber-400 border-amber-400/40' },
  marked_paid: { label: 'Claimed', cls: 'bg-blue-500/20 text-blue-300 border-blue-400/40' },
  received: { label: 'Received', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40' },
  refunded: { label: 'Refunded', cls: 'bg-rose-500/20 text-rose-300 border-rose-400/40' },
};

const authHeaders = () => {
  const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

export default function EventPayReconcileOrganiserPanel({ event, attendees, onRefresh }) {
  const [busyId, setBusyId] = useState(null);
  const eventId = event?.event_id;

  // Bucket counts for the header strip.
  const counts = useMemo(() => {
    const c = { unpaid: 0, marked_paid: 0, received: 0, refunded: 0 };
    (attendees || []).forEach(a => {
      const s = a.payment_status || 'unpaid';
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [attendees]);

  if (!event?.organiser_collects_money) return null;

  const mark = async (userId, action) => {
    setBusyId(`${userId}:${action}`);
    try {
      const url = action === 'received'
        ? `${API}/events/${eventId}/mark-received/${userId}`
        : `${API}/events/${eventId}/refund/${userId}`;
      await axios.post(url, {}, { headers: authHeaders() });
      toast.success(action === 'received' ? 'Marked received' : 'Refunded');
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-2xl border border-primary/30 bg-card/40 p-4 space-y-4" data-testid="organiser-reconcile-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
            <PoundSterling className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold">Reconcile payments</p>
            <p className="text-[11px] text-muted-foreground">Confirm money landed or refund a no-show.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1.5 text-center">
        <div className="rounded-xl bg-amber-500/10 border border-amber-400/30 py-1.5" data-testid="reconcile-count-unpaid">
          <p className="text-base font-black text-amber-300 leading-none">{counts.unpaid}</p>
          <p className="text-[9px] uppercase tracking-wider text-amber-300/70 mt-0.5">Unpaid</p>
        </div>
        <div className="rounded-xl bg-blue-500/10 border border-blue-400/30 py-1.5" data-testid="reconcile-count-claimed">
          <p className="text-base font-black text-blue-200 leading-none">{counts.marked_paid}</p>
          <p className="text-[9px] uppercase tracking-wider text-blue-200/70 mt-0.5">Claimed</p>
        </div>
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/30 py-1.5" data-testid="reconcile-count-received">
          <p className="text-base font-black text-emerald-200 leading-none">{counts.received}</p>
          <p className="text-[9px] uppercase tracking-wider text-emerald-200/70 mt-0.5">Received</p>
        </div>
        <div className="rounded-xl bg-rose-500/10 border border-rose-400/30 py-1.5" data-testid="reconcile-count-refunded">
          <p className="text-base font-black text-rose-200 leading-none">{counts.refunded}</p>
          <p className="text-[9px] uppercase tracking-wider text-rose-200/70 mt-0.5">Refunded</p>
        </div>
      </div>

      <div className="space-y-2">
        {(attendees || []).length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-6" data-testid="reconcile-empty">
            No-one&apos;s on the going list yet.
          </p>
        )}
        {(attendees || []).map(a => {
          const s = a.payment_status || 'unpaid';
          const pill = STATUS_PILLS[s] || STATUS_PILLS.unpaid;
          const proof = a.payment_proof;
          const handle = a.username || a.name || 'Skater';
          return (
            <div
              key={a.user_id}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-border/30 bg-muted/10"
              data-testid={`reconcile-row-${a.user_id}`}
            >
              <Avatar className="w-9 h-9 ring-1 ring-border/40">
                <AvatarImage src={a.photo} alt={handle} />
                <AvatarFallback className="text-xs bg-primary/20 text-primary font-bold">
                  {(handle || '?')[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{handle}</p>
                {proof && s !== 'unpaid' && (
                  <p className="text-[10px] text-muted-foreground truncate">
                    {proof.paid_date}
                    {proof.paid_time ? ` ${proof.paid_time}` : ''}
                    {proof.reference ? ` · ref “${proof.reference}”` : ''}
                    {proof.method ? ` · ${proof.method.replace('_', ' ')}` : ''}
                  </p>
                )}
              </div>
              <Badge
                className={`text-[10px] uppercase tracking-wider border ${pill.cls} flex-shrink-0`}
                data-testid={`reconcile-row-status-${a.user_id}`}
              >
                {pill.label}
              </Badge>
              <div className="flex flex-col gap-1 flex-shrink-0">
                {s !== 'received' && s !== 'refunded' && (
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-[10px] rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-400/40 gap-1"
                    onClick={() => mark(a.user_id, 'received')}
                    disabled={busyId === `${a.user_id}:received`}
                    data-testid={`reconcile-mark-received-${a.user_id}`}
                  >
                    {busyId === `${a.user_id}:received` ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    Received
                  </Button>
                )}
                {s !== 'refunded' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[10px] rounded-full text-rose-300 border-rose-400/40 hover:bg-rose-500/10 gap-1"
                    onClick={() => mark(a.user_id, 'refund')}
                    disabled={busyId === `${a.user_id}:refund`}
                    data-testid={`reconcile-refund-${a.user_id}`}
                  >
                    {busyId === `${a.user_id}:refund` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    Refund
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
